import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Messages } from 'primereact/messages';
import { getFormErrorMessage } from '../../../utils/errors';
import { RHFInputText, RHFCheckbox } from '../../../components/rhf';
import { ModalDialog } from '../../../components/ModalDialog';
import { ButtonAdd, ButtonEdit } from '../../../components/Buttons';
import { FormContainer, FormInput, FormRow, Label } from '../../../components/form';
import type { LanguageAddRequest, LanguageEditRequest, LanguageResponse } from '../../../../api';

export interface LanguageDialogProps {
    isVisible: boolean;
    onHide: () => void;
    onSubmitAdd: (data: LanguageAddRequest) => Promise<void>;
    onSubmitEdit: (data: LanguageEditRequest) => Promise<void>;
    editLanguage: LanguageResponse | null;
    msgs?: React.RefObject<Messages> | null;
    processing: boolean;
}

export function LanguageDialog({
    isVisible,
    onHide,
    onSubmitAdd,
    onSubmitEdit,
    editLanguage,
    msgs,
    processing
}: LanguageDialogProps) {
    const formId = 'manage-language-dialog';
    const isEdit = editLanguage !== null;

    const defaultValues = useMemo<LanguageResponse>(
        () => ({
            language_code: '',
            en_name: '',
            local_name: '',
            active: false
        }),
        []
    );

    const {
        control,
        formState: { errors, isSubmitting },
        handleSubmit,
        reset
    } = useForm<LanguageResponse>({
        disabled: processing,
        defaultValues: defaultValues
    });

    useEffect(() => {
        if (editLanguage) {
            reset({
                language_code: editLanguage.language_code,
                en_name: editLanguage.en_name,
                local_name: editLanguage.local_name,
                active: editLanguage.active
            });
        } else {
        reset(defaultValues);
        }
    }, [editLanguage, reset, defaultValues]);

    useEffect(() => {
        if (!isVisible) {
            msgs?.current?.clear();
        }
    }, [isVisible, msgs]);

    const submitHandler = handleSubmit(async (values: LanguageResponse) => {
        if (isEdit) {
            const editPayload: LanguageEditRequest = {
                en_name: values.en_name,
                local_name: values.local_name,
                active: values.active
            };
            await onSubmitEdit(editPayload);
            return;
        }

        await onSubmitAdd(values);
    });

    return (
        <ModalDialog
            visible={isVisible}
            header={isEdit ? `Upravit jazyk ${editLanguage.en_name}` : `Přidat nový jazyk`}
            style={{ width: 450 }}
            footer={
                <div className='flex-right'>
                    {isEdit ? (
                        <ButtonEdit
                            label='Uložit změny'
                            type='submit'
                            form={formId}
                            disabled={processing || isSubmitting}
                            rounded={false}
                        />
                    ) : (
                        <ButtonAdd
                            label='Vytvořit jazyk'
                            type='submit'
                            form={formId}
                            disabled={processing || isSubmitting}
                            rounded={false}
                        />
                    )}
                </div>
            }
            onHide={onHide}
        >
            <Messages ref={msgs} className='flex-center' />

            <form onSubmit={submitHandler} id={formId}>
                <FormContainer type='dialog'>
                    <FormRow>
                        <FormInput width='sm'>
                            <Label htmlFor='language_code' text='Kód (např. ISO 639-1)' required />
                            <RHFInputText
                                control={control}
                                name='language_code'
                                rules={{ required: 'Vyplňte kód (např. en)' }}
                                placeholder='např. cs'
                                disabled={isEdit}
                            />
                            {getFormErrorMessage(errors, 'language_code')}
                        </FormInput>
                    </FormRow>

                    <FormRow>
                        <FormInput>
                            <Label htmlFor='en_name' text='Anglický název' required />
                            <RHFInputText
                                control={control}
                                name='en_name'
                                rules={{ required: 'Vyplňte název' }}
                                placeholder='např. Czech'
                            />
                            {getFormErrorMessage(errors, 'en_name')}
                        </FormInput>
                    </FormRow>

                    <FormRow>
                        <FormInput>
                            <Label htmlFor='local_name' text='Lokální název' required />
                            <RHFInputText
                                control={control}
                                name='local_name'
                                rules={{ required: 'Vyplňte lokální název' }}
                                placeholder='např. Čeština'
                            />
                            {getFormErrorMessage(errors, 'local_name')}
                        </FormInput>
                    </FormRow>

                    <FormRow>
                        <FormInput className='flex align-items-center gap-2'>
                            <RHFCheckbox
                                control={control}
                                name='active'
                            />
                            <Label htmlFor='active' text='Aktivní' className='mb-0' />
                        </FormInput>
                    </FormRow>
                </FormContainer>
            </form>
        </ModalDialog>
    );
};
